import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import Step from '../../../../../../../features/components/Forms/StepForm/Step'
import RadioBlock from '../../../../../../../components/Form/RadioBlock'
import Field from '../../../../../../../components/Form/Field'
import AdQuarantineField from '../../../../../../ReusableFormFields/AdQuarantineField'

import { lineItemSelector, lineItemWasLoadedSelector } from '../../../../../../../modules/selectors/lineItems'
import { tiktokRightUploadAdDataSelector } from '../../../../../../../modules/selectors/rights'

import {
  BRAND_SAFETY_POSTBID_PARTNER,
  BRAND_SAFETY_VAST_URL,
  brandSafetyPartnerOptions,
  IAS,
  VIEWABILITY_AND_BRAND_SAFETY_OPTION,
  VIEWABILITY_POSTBID_PARTNER,
  VIEWABILITY_VAST_URL,
  viewabilityPartnerOptions
} from '../../fields'
import { OPTION_NO, OPTION_YES } from '../../../../../../../constants/forms'
import { BRAND_SAFETY } from '../../../../../UploadTikTokAdCreative/UploadTikTokAdCreativeForm/LineItemForm/fields'
import { NO_BRAND_SAFETY } from '../../../../../UploadTikTokAdCreative/UploadTikTokAdCreativeForm/LineItemForm/brandSafetyOptions'

const ViewabilityAndBrandSafetyStep = ({ formik }) => {
  const { t } = useTranslation()

  const { values, setFieldValue } = formik

  const lineItem = useSelector(lineItemSelector)
  const lineItemWasLoaded = useSelector(lineItemWasLoadedSelector)
  const { lineItem: rightUploadAdLineItemData } = useSelector(tiktokRightUploadAdDataSelector)

  const parentLineItem = useMemo(() => {
    if (lineItemWasLoaded) {
      return lineItem
    } else {
      return rightUploadAdLineItemData
    }
  }, [lineItem, lineItemWasLoaded, rightUploadAdLineItemData])

  const showBrandSafetyDropdown = parentLineItem[BRAND_SAFETY] !== NO_BRAND_SAFETY

  const stepTitle = useMemo(() => {
    if (showBrandSafetyDropdown) {
      return 'Do you want to track viewability or measure brand safety with a third-party partner?'
    } else {
      return 'Do you want to track viewability with a third-party partner?'
    }
  }, [showBrandSafetyDropdown])

  // Handle radio button change
  const handleRadioChange = useCallback(
    value => {
      // If "No" is selected, reset all related fields
      if (value === OPTION_NO) {
        setFieldValue(VIEWABILITY_POSTBID_PARTNER, '')
        setFieldValue(VIEWABILITY_VAST_URL, '')
        setFieldValue(BRAND_SAFETY_POSTBID_PARTNER, '')
        setFieldValue(BRAND_SAFETY_VAST_URL, '')
      }
    },
    [setFieldValue]
  )

  return (
    <Step stepTitle={stepTitle}>
      <RadioBlock
        setFieldValue={setFieldValue}
        onValueChange={handleRadioChange}
        id={VIEWABILITY_AND_BRAND_SAFETY_OPTION + '_yes'}
        name={VIEWABILITY_AND_BRAND_SAFETY_OPTION}
        value={OPTION_YES}
        selectedValue={values[VIEWABILITY_AND_BRAND_SAFETY_OPTION]}
        label={t('Yes')}
      >
        <p>{t('Select a third party partner to track viewability with (optional)')}</p>
        <Field
          formik={formik}
          name={VIEWABILITY_POSTBID_PARTNER}
          placeholder="Third-party partner"
          options={viewabilityPartnerOptions}
          isClearable={true}
        />
        {values[VIEWABILITY_POSTBID_PARTNER] === IAS && (
          <Field formik={formik} name={VIEWABILITY_VAST_URL} placeholder="Viewability VAST URL" type="text" />
        )}
        {showBrandSafetyDropdown && (
          <>
            <p>{t('Select a third party partner to measure brand safety with (optional)')}</p>
            <Field
              formik={formik}
              name={BRAND_SAFETY_POSTBID_PARTNER}
              placeholder="Third-party partner"
              options={brandSafetyPartnerOptions}
              isClearable={true}
            />
            {values[BRAND_SAFETY_POSTBID_PARTNER] === IAS && (
              <Field formik={formik} name={BRAND_SAFETY_VAST_URL} placeholder="Brand Safety VAST URL" type="text" />
            )}
          </>
        )}
      </RadioBlock>
      <RadioBlock
        setFieldValue={setFieldValue}
        onValueChange={handleRadioChange}
        id={VIEWABILITY_AND_BRAND_SAFETY_OPTION + '_no'}
        name={VIEWABILITY_AND_BRAND_SAFETY_OPTION}
        value={OPTION_NO}
        selectedValue={values[VIEWABILITY_AND_BRAND_SAFETY_OPTION]}
        label={t('No')}
      />
      <AdQuarantineField formik={formik} />
    </Step>
  )
}

export default ViewabilityAndBrandSafetyStep
